import { EllipsisMenu } from 'components/index';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import SearchBar from 'components/SearchBar/SearchBar';
import BaseTable from 'components/Tables/BaseTable';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import Cookies from 'js-cookie';
import { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp, FaEye, FaLock } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Popup from 'reactjs-popup';
import { deleteRole, getAllRoles, updateRoleHierarchy } from 'services/roles/roles.service';

export default function RolesTable({ list, perms, setRoleList, msalInstance }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [rowIdEllipsisOpen, setRowIdEllipsisOpen] = useState({});
  const [showConfirmDeleteRole, setShowConfirmDeleteRole] = useState(false);
  const [showCantRemoveRole, setShowCantRemoveRole] = useState(false);
  const [hierarchy, setHierarchy] = useState({});
  const [hierarchyChanged, setHierarchyChanged] = useState(false);
  const [originalHierarchy, setOriginalHierarchy] = useState({});
  const [yourHighestRoleHierarchyId, setYourHighestRoleHierarchyId] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    setRoleList(
      list.sort((a, b) => {
        if (parseInt(a.role_Id) === -2) return 1; // Move 'a' to the bottom
        if (parseInt(b.role_Id) === -2) return -1; // Move 'b' to the bottom

        if (parseInt(a.role_Id) === -1) return -1; // Move 'a' to the bottom
        if (parseInt(b.role_Id) === -1) return 1; // Move 'b' to the bottom
        if (parseInt(a.role_Id) < 0) return -1; // Move 'a' to the bottom
        if (parseInt(b.role_Id) < 0) return 1; // Move 'b' to the bottom

        return hierarchy[a.role_Id] - hierarchy[b.role_Id];
      }),
    );
  }, [list, hierarchy]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await getAllRoles(msalInstance);

        setRoleList([...res.data.result]);
        buildHierarchy([...res.data.result]);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    if (JSON.stringify(hierarchy) === JSON.stringify(originalHierarchy)) {
      setHierarchyChanged(false);
    }
  }, [hierarchy]);

  const buildHierarchy = (roles) => {
    const rolesToHierarch = [...roles].filter((item) => {
      return parseInt(item.role_Id) >= 0;
    });
    let hierarchy = {};
    rolesToHierarch.forEach((role) => {
      hierarchy[role.role_Id] = role.hierarchy_level;
    });
    let max = 999;

    let l = [...roles];
    const myRoles = l
      .filter((item) => {
        if (item.users?.includes(Cookies.get('username'))) {
          return item.role_Id;
        }
      })
      .map((item) => {
        return item.role_Id;
      });

    rolesToHierarch.forEach((role) => {
      if (myRoles.includes(role.role_Id)) {
        if (role.hierarchy_level <= max) {
          max = role.hierarchy_level;
        }
      } else {
      }
    });
    setYourHighestRoleHierarchyId(max);

    setHierarchy(hierarchy);
    setOriginalHierarchy(hierarchy);
  };

  const handleDeleteRole = async () => {
    const res = await deleteRole(itemToDelete.role_Id);
    setShowConfirmDeleteRole(false);

    //remove roleid entry from hierarchy
    const newHierarchy = { ...hierarchy };
    const deleted = newHierarchy[itemToDelete.role_Id];
    delete newHierarchy[itemToDelete.roleId];

    Object.keys(newHierarchy).forEach((role) => {
      if (newHierarchy[role] > deleted) newHierarchy[role] -= 1;
    });

    setHierarchy(newHierarchy);

    await getAllRoles(msalInstance).then((res) => {
      setRoleList([...res.data.result]);
      setShowConfirmDeleteRole(false);
    });
  };

  const searchFilter = (roles) => {
    if (!roles) return false;

    const search = params.get('search');

    if (search) {
      return (
        roles.role_name
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(search?.toLowerCase().replace(/\s/g, '')) ||
        roles.created_by_email
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(search?.toLowerCase().replace(/\s/g, ''))
      );
    } else return true;
  };

  const sortFunc = (a, b) => {
    if (parseInt(a.role_Id) === -2) return 1; // Move 'a' to the bottom
    if (parseInt(b.role_Id) === -2) return -1; // Move 'b' to the bottom

    if (parseInt(a.role_Id) === -1) return -1; // Move 'a' to the bottom
    if (parseInt(b.role_Id) === -1) return 1; // Move 'b' to the bottom
    if (parseInt(a.role_Id) < 0) return -1; // Move 'a' to the bottom
    if (parseInt(b.role_Id) < 0) return 1; // Move 'b' to the bottom

    return hierarchy[a.role_Id] - hierarchy[b.role_Id];
  };

  const rows = useMemo(() => {
    if (list.length === 0) return [];
    return list
      .filter(searchFilter)
      .sort(sortFunc)
      .map((item, index) => ({
        hierarchy_level: item.hierarchy_level,
        role_name: item.role_name,
        role_count: item.role_count,
        created_by_email: item.created_by_email,
        role_Id: item.role_Id,
        users: item.users,
      }));
  }, [list, hierarchy, params]);

  // Columns
  const columns = useMemo(() => {
    return [
      {
        header: '',
        accessorKey: 'hierarchy_level',
        Cell: ({ row, cell }) => {
          let rank = cell.getValue();
          const role_Id = row.original.role_Id;
          return (
            <div className="d-flex flex-row gap-1 justify-content-start align-items-center text-white">
              {(role_Id >= 0 && perms.accessLevel >= 12) || perms.accessLevel == 8 ? (
                <div
                  style={{
                    width: 50,
                  }}
                >
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    {hierarchy[role_Id] > 0 && (
                      <div
                        onClick={() => {
                          const useList = list.sort(sortFunc);
                          setHierarchyChanged(true);
                          //move this up one and the one above down one
                          const index = useList.findIndex((num, idx, arr) => {
                            if (arr[idx].role_Id === role_Id) return true;
                            else return false;
                          });
                          const newHierarchy = { ...hierarchy };
                          const newRole = { ...useList[index] };
                          const oldRole = { ...useList[index - 1] };
                          newHierarchy[newRole.role_Id] -= 1;
                          newHierarchy[oldRole.role_Id] += 1;
                          setHierarchy(newHierarchy);
                        }}
                      >
                        <FaChevronUp />
                      </div>
                    )}
                    {hierarchy[role_Id] >= 0 ? <div>{hierarchy[role_Id] + 1}</div> : <div></div>}
                    {hierarchy[role_Id] < Object.keys(hierarchy).length - 1 && (
                      <div
                        onClick={() => {
                          const useList = list.sort(sortFunc);
                          setHierarchyChanged(true);
                          //move this down one and the one below up one
                          const index = useList.findIndex((num, idx, arr) => {
                            if (arr[idx].role_Id === role_Id) return true;
                            else return false;
                          });
                          const newHierarchy = { ...hierarchy };
                          const newRole = { ...useList[index] };
                          const oldRole = { ...useList[index + 1] };
                          newHierarchy[newRole.role_Id] += 1;
                          newHierarchy[oldRole.role_Id] -= 1;
                          setHierarchy(newHierarchy);
                        }}
                      >
                        <FaChevronDown />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  className="d-flex justify-content-center opacity-50"
                  style={{
                    width: 50,
                  }}
                >
                  {perms.accessLevel === 10 &&
                  role_Id !== -2 &&
                  perms.accessLevel === 10 &&
                  hierarchy[role_Id] > yourHighestRoleHierarchyId ? (
                    <div></div>
                  ) : (
                    <FaLock />
                  )}
                </div>
              )}
            </div>
          );
        },
        size: 90,
      },
      {
        header: 'Roles',
        accessorKey: 'role_name',
        Cell: ({ cell }) => {
          const role = cell.getValue();
          return <div className="text-white fs-6">{role}</div>;
        },
        enableSorting: false,
      },
      {
        header: 'Users',
        accessorKey: 'role_count',
        Cell: ({ cell, row }) => {
          const count = cell.getValue();
          const userList = row.original.users ?? [];
          return (
            <Popup
              trigger={<div className="text-white fs-6 d-block cursor-pointer">{count}</div>}
              position="top center"
              on="hover"
            >
              <div
                className="d-flex flex-column bg-primary rounded-2 p-2 gap-1 overflow-y-auto customScrollbar-Y"
                style={{ minWidth: 160, maxHeight: 200 }}
              >
                {userList.length > 0 ? (
                  userList.map((u) => (
                    <div key={u} className="text-white text-break">
                      {u}
                    </div>
                  ))
                ) : (
                  <div className="text-white">No users</div>
                )}
              </div>
            </Popup>
          );
        },
        size: 90,
        enableSorting: false,
      },
      {
        header: 'Created By',
        accessorKey: 'created_by_email',
        Cell: ({ cell }) => {
          const creator = cell.getValue();
          return <div className="text-white text-break text-wrap fs-6">{creator}</div>;
        },
        size: 200,
        enableSorting: false,
      },
      {
        header: '',
        accessorKey: 'icon',
        Cell: ({ row, cell }) => {
          const role_Id = row.original.role_Id;
          const hierarchy_level = row.original.hierarchy_level;
          return (
            <div className="align-content-center justify-content-start">
              {(perms.testPermission([8, 9, 10, 11, 12, 13], ['EditRole']) ||
                perms.testPermission([8, 9, 10, 11, 12, 13], ['EditRole']) ||
                perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteRole'])) &&
              //or user is 12 or 13
              role_Id >= 0 &&
              (perms.accessLevel === 12 ||
                perms.accessLevel === 13 ||
                hierarchy_level > yourHighestRoleHierarchyId) ? (
                <EllipsisMenu
                  options={[
                    {
                      component: (
                        <>
                          {perms.testPermission([8, 9, 10, 11, 12, 13], ['EditRole']) && (
                            <Button
                              className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                              onClick={() => navigate(`/system/editrole?roleid=${role_Id}`)}
                              variant="secondary"
                            >
                              Edit
                            </Button>
                          )}
                        </>
                      ),
                    },
                    {
                      component: (
                        <>
                          {perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteRole']) && (
                            <Button
                              className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                              onClick={() => {
                                //TODO: Follow and find why remove doesnt work
                                row.original.role_count > 0
                                  ? setShowCantRemoveRole(true)
                                  : setShowConfirmDeleteRole(true);
                                setItemToDelete(row.original);
                              }}
                              variant="danger"
                            >
                              Remove
                            </Button>
                          )}
                        </>
                      ),
                    },
                  ]}
                  open={rowIdEllipsisOpen[role_Id]}
                  containerClassname="bg-transparent"
                  onOpen={() =>
                    setRowIdEllipsisOpen({
                      ...rowIdEllipsisOpen,
                      [role_Id]: true,
                    })
                  }
                  onClose={() =>
                    setRowIdEllipsisOpen({
                      ...rowIdEllipsisOpen,
                      [role_Id]: false,
                    })
                  }
                />
              ) : (
                <Popup
                  trigger={
                    <Button
                      className="p-0 text-white rounded-circle"
                      onClick={() => {
                        navigate(`/system/viewrole?roleid=${role_Id}`);
                      }}
                      variant="secondary"
                      style={{
                        width: 32,
                        height: 32,
                      }}
                    >
                      <FaEye />
                    </Button>
                  }
                  position="top center"
                  on="hover"
                  closeOnDocumentClick
                >
                  <div
                    className="d-flex flex-column gap-1 fs-6 fw-normal bg-primary rounded cursor-pointer"
                    onClick={() => {
                      navigate(`/system/viewrole?roleid=${role_Id}`);
                    }}
                  >
                    <div
                      className="d-flex flex-row justify-content-between p-1 px-2"
                      style={{ backgroundColor: '#2c373b' }}
                    >
                      <div>View Role</div>
                    </div>
                  </div>
                </Popup>
              )}
            </div>
          );
        },
        size: 60,
        enableSorting: false,
      },
    ];
  }, [list, hierarchy, setRoleList, perms]);

  return (
    <div className="w-100 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      <div className="w-100">
        <div className="w-100 w-lg-25 mb-2 justify-content-end ms-auto">
          <SearchBar className="bg-primary-highlight rounded-3" />
        </div>
        {list.length === 0 && <WidgetLoadingAnimation />}
        {perms.accessLevel >= 12 && hierarchyChanged && (
          <div className="d-flex flex-row mt-2">
            <Button
              className="p-2 rounded ms-3 justify-content-center align-items-center d-flex"
              onClick={async () => {
                const res = await updateRoleHierarchy(hierarchy);
                setHierarchyChanged(false);
              }}
            >
              <div>Save</div>
            </Button>
            <Button
              className="d-flex align-items-center justify-content-center p-2 rounded ms-3"
              onClick={async () => {
                setHierarchy(originalHierarchy);
                setHierarchyChanged(false);
              }}
            >
              <div>Discard</div>
            </Button>
            <div className="ms-2 align-self-center">
              Careful! You have unsaved changes to the Organization's Role Hierarchy, this controls
              the highest-ranked role of all Client Users.
            </div>
          </div>
        )}
      </div>
      <div>
        {' '}
        <div>
          {showConfirmDeleteRole && (
            <ConfirmationModal
              show={showConfirmDeleteRole}
              handleShow={() => setShowConfirmDeleteRole(false)}
              bodyTxt={`Are you sure you want to remove this role?`}
              cancelBtnTxt={'Cancel'}
              confirmBtnTxt={'Yes'}
              confirmHandler={handleDeleteRole}
            />
          )}
          {showCantRemoveRole && (
            <ConfirmationModal
              show={showCantRemoveRole}
              handleShow={() => setShowCantRemoveRole(false)}
              bodyTxt={`The ${itemToDelete.role_name} Role cannot be removed with ${
                itemToDelete.role_count
              } assigned User${
                itemToDelete.role_count > 1 ? 's' : ''
              }. Remove all users from this role and try again.`}
              confirmBtnTxt={['Ok']}
              confirmHandler={setShowCantRemoveRole}
            />
          )}
        </div>
      </div>
      <div className="w-100 flex-grow-1 overflow-hidden" style={{ minHeight: 0 }}>
        {list && (
          <BaseTable
            rows={rows}
            columns={columns}
            altRowSettings={{
              alternate: true,
              color1: '#222a2d',
              color2: '#2C373B',
            }}
            customStyles={{ container: { height: '100%' } }}
            muiTablePaperProps={{
              sx: { height: '100%', display: 'flex', flexDirection: 'column' },
            }}
            muiTableContainerProps={{ sx: { flex: 1, overflow: 'auto' } }}
          />
        )}
      </div>
    </div>
  );
}
